import { lenis } from './Scroll';
import { bindOnce } from '../utils/bindOnce';

const CONFIG = {
  headerGap: 16,
  bookingFallback: '/appointment/booking/',
};

function fertilityCheck() {
  const root = document.querySelector('.p-fertility-check');
  if (!root || !bindOnce(root, 'fertilityCheck')) return;

  const intro = root.querySelector('#fertility-hero-section');
  const start = root.querySelector('#fertility-start-screen');
  const result = root.querySelector('#fertility-result-screen');
  const progress = root.querySelector('.p-fertility-check__progress');
  const questions = Array.from(root.querySelectorAll('.p-fertility-check__quiz-container'));
  const bookingHref = result?.dataset.bookingHref || CONFIG.bookingFallback;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ans = {};
  let current = 0;

  const headerOffset = () => {
    const headerH = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--size-header-h')) || 0;
    return headerH + CONFIG.headerGap;
  };

  const scrollToPin = (target) => {
    if (!target) return;
    const offset = -headerOffset();
    if (typeof lenis.resize === 'function') lenis.resize();
    if (reduceMotion) {
      const top = window.scrollY + target.getBoundingClientRect().top + offset;
      window.scrollTo(0, Math.max(top, 0));
      return;
    }
    lenis.scrollTo(target, {
      offset,
      immediate: true,
      force: true,
    });
  };

  const moveTo = (scrollTarget, focusTarget) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToPin(scrollTarget);
        focusTarget?.focus({ preventScroll: true });
      });
    });
  };

  const showQuestion = (n) => {
    current = n;
    questions.forEach((question, index) => {
      question.hidden = index !== n - 1;
    });
    const question = questions[n - 1];
    progress.querySelector('[data-step]').textContent = String(n);
    progress.querySelector('[data-category]').textContent = question.querySelector('.p-fertility-check__q-cat-label').textContent;
    const track = progress.querySelector('progress');
    track.value = n;
    track.textContent = `${n} / 10`;
    moveTo(progress, question.querySelector('h2'));
  };

  const showResult = () => {
    if (questions.some((_, index) => ans[index + 1] === undefined)) return;
    const score = Object.values(ans).reduce((total, value) => total + value, 0);
    questions.forEach((question) => {
      question.hidden = true;
    });
    progress.hidden = true;
    result.hidden = false;

    let typeClass;
    let eyebrow;
    let typeTag;
    let icon;
    let headline;
    let desc;
    let advice;
    let quote;

    if (score <= 4) {
      typeClass = '-type-d';
      eyebrow = 'Type D';
      typeTag = 'まずは知ることから';
      icon = '&#x1f331;';
      headline = '今が、<em>はじまり</em>の時。';
      desc = '現時点では特に心配な要因は見られません。ただ、「気になる」という気持ちがあること自体、大切なサインです。妊娠は準備が早いほど選択肢が広がります。まずは一度、専門医に気軽に話を聞いてみることをおすすめします。';
      advice = ['妊娠を望んでいるなら、早めの情報収集が安心につながります', '基礎体温の記録など、今日からできることを始めましょう', '不安があれば、初診での相談のみもお気軽にどうぞ'];
      quote = '妊娠を「まだ先のこと」と思っていても、あなたの体は今この瞬間も変化しています。「なんとなく不安」——その感覚はとても正しい直感です。何も問題がなかったとしても、知っておくことは力になります。';
    } else if (score <= 9) {
      typeClass = '-type-c';
      eyebrow = 'Type C';
      typeTag = '専門的な確認が助けになる段階';
      icon = '&#x1f499;';
      headline = 'あなたの体を、<em>もっと知る</em>タイミングです。';
      desc = 'いくつかの気になるポイントがあります。妊活をより効果的に進めるために、一度専門的な検査を受けてみることをおすすめします。原因がわかれば、対応策もはっきりします。知ることは、前進することです。';
      advice = ['月経や生活習慣など、気になる部分を専門医に相談しましょう', '基本的な検査（ホルモン検査・子宮卵管造影など）が助けになります', 'パートナーと一緒に受診することで、原因の特定がより確かになります'];
      quote = '「様子を見る」よりも「知る」方が、ずっと安心できます。妊娠しにくい理由がある場合、早めに見つけるほど対処の選択肢が広がります。30年間、私が最もよく聞く言葉は「もっと早く来ればよかった」という言葉です。';
    } else if (score <= 14) {
      typeClass = '-type-b';
      eyebrow = 'Type B';
      typeTag = '早めの専門相談を';
      icon = '&#x1f536;';
      headline = 'あなたには、<em>専門医の目</em>が必要です。';
      desc = '複数の気になる要因が重なっています。妊活を続けているにも関わらず思うような結果が出ない、あるいは不安やストレスが積み重なっているなら、ひとりで抱え込まないでください。専門クリニックへの相談が、大きな転機になる可能性があります。';
      advice = ['原因不明の不妊は、検査ではじめてわかることが多くあります', '保険適用でリーズナブルに治療をスタートできる場合があります', '転院・セカンドオピニオンという選択肢も有効です'];
      quote = 'なぜ妊娠しないのか、その理由を知ることが最初の一歩です。私は30年間、「原因がわからない」と言われてきた方々の妊娠をお手伝いしてきました。あなたにも、必ず理由があります。そして理由がわかれば、道は開けます。';
    } else {
      typeClass = '-type-a';
      eyebrow = 'Type A';
      typeTag = '今すぐ専門医への相談を';
      icon = '&#x1f534;';
      headline = '今が、<em>動くべき</em>タイミングです。';
      desc = '複数の重要なリスク因子が重なっています。年齢・妊活期間・治療歴・精神的な負荷——これらが重なるほど、専門的なサポートの重要性は高まります。一人で悩む時間を、今すぐ「知る」時間に変えてください。';
      advice = ['今の状態を最短で打開するためには、専門的な診断が不可欠です', '他院での治療に行き詰まりを感じているなら、セカンドオピニオンが有効です', '精神的なサポートも含め、クリニック全体でサポートします'];
      quote = '長く、辛い道のりを歩んでこられたことは、私にはわかります。「なぜ妊娠しないのか」——30年間、私はその答えを探し続けてきました。あなたの「なぜ」にも、必ず答えはあります。最後の砦として、ぜひ一度お話を聞かせてください。';
    }

    result.innerHTML = `
      <div class="p-fertility-check__result-wrap ${typeClass}">
        <div class="p-fertility-check__result-strip"></div>
        <div class="p-fertility-check__result-top">
          <div class="p-fertility-check__result-eyebrow">${eyebrow}</div>
          <div class="p-fertility-check__result-type-tag">${typeTag}</div>
          <div class="p-fertility-check__result-icon" aria-hidden="true">${icon}</div>
          <h2 class="p-fertility-check__result-headline" id="fertility-result-title" tabindex="-1">${headline}</h2>
        </div>
        <div class="p-fertility-check__result-body-section">
          <p class="p-fertility-check__result-desc">${desc}</p>
          <div class="p-fertility-check__advice-block">
            <h3 class="c-head05 -greenGrad03">アドバイス</h3>
            <ul class="p-fertility-check__advice-list">
              ${advice.map((item) => `<li>${item}</li>`).join('')}
            </ul>
          </div>
        </div>
        <div class="p-fertility-check__doctor-section">
          <div class="p-fertility-check__doctor-label">Director's Message</div>
          <div>
            <div class="p-fertility-check__doctor-quote">${quote}</div>
            <div class="p-fertility-check__doctor-sig">大阪NewARTクリニック　院長 富山　和仁</div>
          </div>
        </div>
        <div class="p-fertility-check__cta-section">
          <h3 class="c-head04 -sm">まずは、院長に<br>話を聞かせてください。</h3>
          <p>初診は予約制です。不安なこと、これまでの経緯、<br>どんなことでも構いません。</p>
          <div class="c-btn01 -center"><a class="c-btn01__link" href="${bookingHref}"><p class="c-btn01__link-txt">初診ご予約</p></a></div>
          <button type="button" class="p-fertility-check__btn-retry" data-action="retry">もう一度チェックする</button>
          <p class="p-fertility-check__cta-note">
            ※ 初診時はこれまでの検査データ・検査結果をお持ちいただくと、より詳しいご相談が可能です。<br>
            ※ 保険診療・自由診療どちらもご相談いただけます。
          </p>
        </div>
      </div>
    `;

    moveTo(result, result.querySelector('h2'));
  };

  root.addEventListener('change', (event) => {
    const input = event.target.closest('input[data-question]');
    if (!input || Number(input.dataset.question) !== current) return;
    ans[current] = Number(input.value);
    questions[current - 1].querySelector('[data-action="next"]').disabled = false;
  });

  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-action]');
    if (!button || button.disabled) return;

    if (button.dataset.action === 'start') {
      intro.hidden = true;
      start.hidden = true;
      progress.hidden = false;
      showQuestion(1);
      return;
    }

    if (button.dataset.action === 'next' && ans[current] !== undefined) {
      if (current === questions.length) showResult();
      else showQuestion(current + 1);
      return;
    }

    if (button.dataset.action === 'retry') {
      Object.keys(ans).forEach((key) => {
        delete ans[key];
      });
      root.querySelectorAll('input[data-question]').forEach((input) => {
        input.checked = false;
      });
      root.querySelectorAll('[data-action="next"]').forEach((next) => {
        next.disabled = true;
      });
      questions.forEach((question) => {
        question.hidden = true;
      });
      result.hidden = true;
      result.innerHTML = '';
      progress.hidden = true;
      intro.hidden = false;
      start.hidden = false;
      current = 0;
      moveTo(intro, start.querySelector('[data-action="start"]'));
    }
  });
}

export { fertilityCheck };
